import React, { FC } from 'react';
import { formatDate } from 'utils/format';
import { Box, Button, Typography } from '@mui/material';
import { useQuery } from '@apollo/client/react';
import { commentsTemplate } from 'apollo/templates/order';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { OrderEvent } from 'models/order-event';
import Loader from 'components/shared/loader/loader.component';
import { useTranslation } from 'react-i18next';
import { InvoicingState, Order } from 'models/order';
import InfoText from 'components/shared/info-text.component';

type OrderCommentsProps = {
    order: Order;
    redirectToModifyOrder: (isResend: boolean) => void;
    versionCheck: boolean;
    isExternalTechnician: boolean;
    isCustomer: boolean;
};
const OrderComments: FC<OrderCommentsProps> = ({
    order,
    redirectToModifyOrder,
    versionCheck,
    isExternalTechnician,
    isCustomer,
}: OrderCommentsProps) => {
    const { t: translate } = useTranslation(['orders', 'common']);
    const { showBoundary } = useDenthubErrorBoundary();

    const modifyLocked = () => {
        return order.isProductionFinished || order.invoicingState !== InvoicingState.NOT_READY;
    };

    const { loading, data } = useQuery(commentsTemplate, {
        fetchPolicy: 'cache-and-network',
        variables: {
            orderId: order._id,
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const comments: OrderEvent[] = data?.comments ?? [];

    if (loading) {
        return <Loader />;
    }

    return (
        <Box>
            {comments.length ? (
                <Box>
                    {comments.map((comment, index) => (
                        <Box key={index} mb={2}>
                            <Box>
                                <Typography variant="subtitle2">
                                    {formatDate(new Date(comment.date), 'datetime')} - {comment.user?.fullName} (
                                    {translate('orders:VERSION')} {comment.version})
                                </Typography>
                            </Box>
                            <Box
                                dangerouslySetInnerHTML={{
                                    __html: (comment.message || '')
                                        // convert \n into <br/>
                                        .replace(/\n/g, '<br/>'),
                                }}
                            />
                        </Box>
                    ))}
                </Box>
            ) : (
                <InfoText text={translate('orders:NO_COMMENTS_AVAILABLE')} />
            )}
            {!isExternalTechnician && versionCheck && !modifyLocked() && (!order.isCreatedByLab || !isCustomer) && (
                <Box>
                    <Button
                        onClick={() => redirectToModifyOrder(false)}
                        data-test="order-comment-section.add-comment-btn"
                    >
                        {translate('orders:ADD_COMMENT')}
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default OrderComments;
